import React, { createContext, useState, useEffect, useContext } from 'react'

import { AuthInstance, EtpInstance } from './'
import {
  logError,
  createTokenSession,
  defaultTokenState,
  createUserSession,
} from './utils'
import config from '../../config'

const {
  endpoints: {
    authUrl,
    accountUrl,
    profileUrl,
    subscriptionUrl,
    benefitsUrl,
    eligibilityUrl,
  },
} = config

const SessionContext = createContext()
const useSessionContext = () => useContext(SessionContext)

const SessionConsumer = SessionContext.Consumer

const SessionProvider = ({ children }) => {
  const [externalId, setExternalId] = useState(null)
  const [tokenInfo, setTokenInfo] = useState(defaultTokenState)
  const [accountInfo, setAccountInfo] = useState({})
  const [profileInfo, setProfileInfo] = useState({})
  const [eligibleInfo, setEligibleInfo] = useState({})
  const [benefitsInfo, setBenefitsInfo] = useState({})
  const [isAuthResolved, setIsAuthResolved] = useState(null)

  useEffect(() => {
    getToken()
  }, [])

  useEffect(() => {
    if (tokenInfo.accessToken && tokenInfo.accountId) {
      getAccountInfo()
      getProfileInfo()
    }
  }, [tokenInfo])

  useEffect(() => {
    if (externalId) {
      getEligibilityInfo()
      getBenefitsInfo()
    }
  }, [externalId])

  const getToken = async () => {
    try {
      const response = await AuthInstance.post(
        authUrl,
        {},
        { isLoggedToken: true, getToken: true }
      )
      const { access_token, country, account_id } = response.data

      setTokenInfo({
        ...tokenInfo,
        ...createTokenSession({ access_token, country, account_id }),
      })
      setIsAuthResolved(true)
    } catch (error) {
      try {
        const response = await AuthInstance.post(
          authUrl,
          {},
          { isLoggedToken: false, getToken: true }
        )
        const { access_token, country } = response.data

        setTokenInfo({
          ...tokenInfo,
          ...createTokenSession({ access_token, country }),
        })
        setIsAuthResolved(false)
      } catch (error) {
        logError(error)
      }
    }
  }

  const getAccountInfo = async () => {
    try {
      const response = await EtpInstance.get(accountUrl, {
        accessToken: tokenInfo.accessToken,
      })
      const { data } = response
      setAccountInfo(data)
      setExternalId(data.external_id)
    } catch (error) {
      logError(error)
    }
  }

  const getProfileInfo = async () => {
    try {
      const response = await EtpInstance.get(profileUrl, {
        accessToken: tokenInfo.accessToken,
      })
      const { data } = response

      // Process the new multi-profile response structure
      if (data.profiles && Array.isArray(data.profiles)) {
        const primaryProfile = data.profiles.find(
          (profile) => profile.is_primary
        )
        const selectedProfile = data.profiles.find(
          (profile) => profile.is_selected
        )

        if (data.profiles.length === 1) {
          // If there's only one profile, use all data from that profile
          const profile = data.profiles[0]
          setProfileInfo({
            ...data, // Keep top-level properties like tier_max_profiles, max_profiles
            ...profile, // Spread all profile properties
            username: primaryProfile?.username || null, // Username always comes from primary profile
          })
        } else {
          // Multiple profiles: get email and username from primary, other data from selected

          setProfileInfo({
            ...data, // Keep top-level properties like tier_max_profiles, max_profiles
            email: primaryProfile?.email || '',
            username: primaryProfile?.username || null,
            profile_id: selectedProfile?.profile_id || null,
            profile_name: selectedProfile?.profile_name || null,
            avatar: selectedProfile?.avatar || null,
            // Include other properties from selected profile that might be needed
            maturity_rating: selectedProfile?.maturity_rating || null,
            extended_maturity_rating:
              selectedProfile?.extended_maturity_rating || {},
            preferred_communication_language:
              selectedProfile?.preferred_communication_language || null,
            preferred_content_audio_language:
              selectedProfile?.preferred_content_audio_language || null,
            preferred_content_subtitle_language:
              selectedProfile?.preferred_content_subtitle_language || null,
            profile_type: selectedProfile?.profile_type || null,
          })
        }
      } else {
        // Fallback for old response format (backwards compatibility)
        setProfileInfo(data)
      }
    } catch (error) {
      logError(error)
    }
  }

  const getEligibilityInfo = async () => {
    try {
      const response = await EtpInstance.get(
        `${subscriptionUrl}/${externalId}${eligibilityUrl}`,
        { accessToken: tokenInfo.accessToken }
      )
      const { data } = response
      setEligibleInfo(data)
    } catch (error) {
      logError(error)
    }
  }

  const getBenefitsInfo = async () => {
    try {
      const response = await EtpInstance.get(
        `${subscriptionUrl}/${externalId}${benefitsUrl}`,
        { accessToken: tokenInfo.accessToken }
      )
      const { data } = response
      setBenefitsInfo(data)
    } catch (error) {
      logError(error)
    }
  }

  const state = {
    isAuthResolved,
    ...createUserSession({
      externalId,
      tokenInfo,
      accountInfo,
      profileInfo,
      eligibleInfo,
      benefitsInfo,
    }),
  }

  return (
    <SessionContext.Provider value={state}>{children}</SessionContext.Provider>
  )
}

export { SessionContext, SessionConsumer, SessionProvider, useSessionContext }
